// Planner settings (bring your own key): Claude via the Anthropic API, or any
// OpenAI-compatible endpoint. Keys are write-only: the server only ever
// returns whether one is set and its last four characters.

import type { Effort, KeyState, PlannerProvider, PlannerSettingsView } from "@fistos/shared/api";
import { Check, Copy, KeyRound, Server } from "lucide-solid";
import { createResource, createSignal, Show, type JSX } from "solid-js";
import { createStore } from "solid-js/store";
import { toast } from "somoto";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import {
  SegmentedControl,
  SegmentedControlIndicator,
  SegmentedControlItem,
  SegmentedControlItemInput,
  SegmentedControlItemLabel,
  SegmentedControlItems,
  SegmentedControlList,
} from "@/components/ui/segmented-control";
import { Select, SelectContent, SelectItem, SelectPortal, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch, SwitchControl, SwitchLabel, SwitchThumb, SwitchInput } from "@/components/ui/switch";
import { TextField, TextFieldDescription, TextFieldInput, TextFieldLabel } from "@/components/ui/text-field";
import { api, errorText } from "@/lib/api";

const EFFORTS: { value: Effort; label: string }[] = [
  { value: "low", label: "Low — fastest" },
  { value: "medium", label: "Medium (default)" },
  { value: "high", label: "High" },
  { value: "xhigh", label: "Extra high" },
  { value: "max", label: "Max" },
];

function keyHint(k: KeyState): string {
  if (k.source === "stored") return `Stored key ending in …${k.hint}. Leave empty to keep it.`;
  if (k.source === "env") return `Using the server's environment key (…${k.hint}). Enter one to override.`;
  return "No key set.";
}

function Field(props: { label: string; description?: JSX.Element; children: JSX.Element }) {
  return (
    <div class="grid gap-2">
      <div class="text-sm font-medium">{props.label}</div>
      {props.children}
      <Show when={props.description}>
        <div class="text-muted-foreground text-xs">{props.description}</div>
      </Show>
    </div>
  );
}

function PlannerCard(props: { initial: PlannerSettingsView }) {
  const [view, setView] = createSignal(props.initial);
  const [form, setForm] = createStore({
    provider: props.initial.provider as PlannerProvider,
    aModel: props.initial.anthropic.model,
    aEffort: props.initial.anthropic.effort,
    aKey: "",
    oBase: props.initial.openai.baseUrl,
    oModel: props.initial.openai.model,
    oKey: "",
    oVision: props.initial.openai.vision,
  });
  const [busy, setBusy] = createSignal(false);
  const [test, setTest] = createSignal<{ ok: boolean; message: string } | null>(null);

  const save = async (): Promise<boolean> => {
    setBusy(true);
    setTest(null);
    try {
      const v = await api.savePlannerSettings({
        provider: form.provider,
        anthropic: { model: form.aModel, effort: form.aEffort, ...(form.aKey ? { apiKey: form.aKey } : {}) },
        openai: {
          baseUrl: form.oBase,
          model: form.oModel,
          vision: form.oVision,
          ...(form.oKey ? { apiKey: form.oKey } : {}),
        },
      });
      setView(v);
      setForm({ aKey: "", oKey: "" });
      toast.success("Planner settings saved");
      return true;
    } catch (e) {
      toast.error(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  const clearKey = async (which: "anthropic" | "openai") => {
    setBusy(true);
    try {
      const v = view();
      const next = await api.savePlannerSettings({
        provider: v.provider,
        anthropic: {
          model: v.anthropic.model,
          effort: v.anthropic.effort,
          ...(which === "anthropic" ? { apiKey: "" } : {}),
        },
        openai: {
          baseUrl: v.openai.baseUrl,
          model: v.openai.model,
          vision: v.openai.vision,
          ...(which === "openai" ? { apiKey: "" } : {}),
        },
      });
      setView(next);
      toast.success("Key removed");
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const runTest = async () => {
    if (!(await save())) return;
    setBusy(true);
    try {
      setTest(await api.testPlanner());
    } catch (e) {
      setTest({ ok: false, message: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle class="flex items-center gap-2">
          <KeyRound class="size-4" /> Planner model
        </CardTitle>
        <CardDescription>
          The phone runs no model. Each step of a task goes to this server, which asks the model you configure here.
          Bring your own key; screen contents and messages of a task are sent to that provider.
        </CardDescription>
      </CardHeader>
      <CardContent class="grid gap-5">
        <SegmentedControl value={form.provider} onChange={(v) => setForm("provider", v as PlannerProvider)}>
          <SegmentedControlList class="w-full sm:w-fit">
            <SegmentedControlIndicator />
            <SegmentedControlItems>
              <SegmentedControlItem value="anthropic">
                <SegmentedControlItemInput />
                <SegmentedControlItemLabel>Anthropic Claude</SegmentedControlItemLabel>
              </SegmentedControlItem>
              <SegmentedControlItem value="openai">
                <SegmentedControlItemInput />
                <SegmentedControlItemLabel>OpenAI-compatible</SegmentedControlItemLabel>
              </SegmentedControlItem>
            </SegmentedControlItems>
          </SegmentedControlList>
        </SegmentedControl>

        <Show when={form.provider === "anthropic"}>
          <div class="grid gap-4 *:min-w-0 sm:grid-cols-2">
            <TextField class="sm:col-span-2" value={form.aKey} onChange={(v) => setForm("aKey", v)}>
              <TextFieldLabel>API key</TextFieldLabel>
              <div class="flex gap-2">
                <TextFieldInput type="password" autocomplete="off" placeholder="sk-ant-…" class="flex-1" />
                <Show when={view().anthropic.key.source === "stored"}>
                  <Button variant="outline" disabled={busy()} onClick={() => clearKey("anthropic")}>
                    Remove
                  </Button>
                </Show>
              </div>
              <TextFieldDescription>{keyHint(view().anthropic.key)}</TextFieldDescription>
            </TextField>
            <TextField value={form.aModel} onChange={(v) => setForm("aModel", v)}>
              <TextFieldLabel>Model</TextFieldLabel>
              <TextFieldInput placeholder="claude-opus-5-5" />
            </TextField>
            <Field
              label="Effort"
              description="Higher effort thinks longer per step: better on hard screens, slower and costlier."
            >
              <Select<(typeof EFFORTS)[number]>
                options={EFFORTS}
                optionValue="value"
                optionTextValue="label"
                value={EFFORTS.find((e) => e.value === form.aEffort)}
                onChange={(o) => o && setForm("aEffort", o.value)}
                itemComponent={(p) => <SelectItem item={p.item}>{p.item.rawValue.label}</SelectItem>}
              >
                <SelectTrigger class="w-full">
                  <SelectValue<(typeof EFFORTS)[number]>>{(s) => s.selectedOption()?.label}</SelectValue>
                </SelectTrigger>
                <SelectPortal>
                  <SelectContent />
                </SelectPortal>
              </Select>
            </Field>
          </div>
          <p class="text-muted-foreground text-xs">
            Requests use adaptive thinking and Anthropic's server-side fallback model if Claude declines a step.
          </p>
        </Show>

        <Show when={form.provider === "openai"}>
          <div class="grid gap-4 *:min-w-0 sm:grid-cols-2">
            <TextField class="sm:col-span-2" value={form.oBase} onChange={(v) => setForm("oBase", v)}>
              <TextFieldLabel>Base URL</TextFieldLabel>
              <TextFieldInput placeholder="https://api.openai.com/v1" />
              <TextFieldDescription>
                Any Chat Completions endpoint with function calling: OpenAI, OpenRouter, vLLM, Ollama (…/v1), LiteLLM.
              </TextFieldDescription>
            </TextField>
            <TextField class="sm:col-span-2" value={form.oKey} onChange={(v) => setForm("oKey", v)}>
              <TextFieldLabel>API key</TextFieldLabel>
              <div class="flex gap-2">
                <TextFieldInput
                  type="password"
                  autocomplete="off"
                  placeholder="Optional for local servers"
                  class="flex-1"
                />
                <Show when={view().openai.key.source === "stored"}>
                  <Button variant="outline" disabled={busy()} onClick={() => clearKey("openai")}>
                    Remove
                  </Button>
                </Show>
              </div>
              <TextFieldDescription>{keyHint(view().openai.key)}</TextFieldDescription>
            </TextField>
            <TextField value={form.oModel} onChange={(v) => setForm("oModel", v)} required>
              <TextFieldLabel>Model</TextFieldLabel>
              <TextFieldInput placeholder="Model id at that endpoint" />
            </TextField>
            <Switch
              class="flex items-center gap-2 self-end pb-2"
              checked={form.oVision}
              onChange={(v) => setForm("oVision", v)}
            >
              <SwitchInput />
              <SwitchControl>
                <SwitchThumb />
              </SwitchControl>
              <SwitchLabel>Send screenshots (model has vision)</SwitchLabel>
            </Switch>
          </div>
        </Show>

        <Show when={test()}>
          {(t) => (
            <Alert variant={t().ok ? "default" : "destructive"}>
              <AlertDescription class="flex items-center gap-2">
                <Show when={t().ok}>
                  <Check class="text-success size-4 shrink-0" />
                </Show>
                {t().message}
              </AlertDescription>
            </Alert>
          )}
        </Show>
      </CardContent>
      <CardFooter class="justify-end gap-2 border-t">
        <Button variant="outline" disabled={busy()} onClick={runTest}>
          Save & test
        </Button>
        <Button disabled={busy()} onClick={save}>
          Save
        </Button>
      </CardFooter>
    </Card>
  );
}

function CopyRow(props: { label: string; value: string }) {
  const [done, setDone] = createSignal(false);
  return (
    <div class="grid min-w-0 gap-1.5">
      <div class="text-muted-foreground text-xs font-medium">{props.label}</div>
      <div class="bg-muted/50 flex items-center gap-2 rounded-md border px-2.5 py-1.5">
        <code class="min-w-0 flex-1 truncate text-xs">{props.value}</code>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Copy ${props.label}`}
          onClick={async () => {
            await navigator.clipboard.writeText(props.value);
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          }}
        >
          <Show when={done()} fallback={<Copy />}>
            <Check />
          </Show>
        </Button>
      </div>
    </div>
  );
}

export function SettingsPage() {
  const [planner] = createResource(() => api.plannerSettings());
  const [server] = createResource(() => api.server());
  return (
    <div class="max-w-3xl space-y-6">
      <div>
        <h1 class="text-2xl font-semibold tracking-tight">Settings</h1>
        <p class="text-muted-foreground text-sm">Planner model and server identity.</p>
      </div>
      <Show when={planner()} fallback={<Card class="h-64 animate-pulse" />}>
        {(p) => <PlannerCard initial={p()} />}
      </Show>
      <Card>
        <CardHeader>
          <CardTitle class="flex items-center gap-2">
            <Server class="size-4" /> Device gateway
          </CardTitle>
          <CardDescription>
            Phones connect out to this address and trust only this certificate pin. Both are part of every enrollment
            code.
          </CardDescription>
        </CardHeader>
        <CardContent class="grid gap-3">
          <Show when={server()}>
            {(s) => (
              <>
                <CopyRow label="Gateway URL (fistos_server_url)" value={s().gatewayUrl} />
                <CopyRow label="CA pin (fistos_server_ca)" value={s().pin} />
                <div class="text-muted-foreground text-xs">Server version {s().version}</div>
              </>
            )}
          </Show>
        </CardContent>
      </Card>
    </div>
  );
}
